import React, { useState } from 'react';
import '../styles/solutions.css';

const SOLUTIONS = [
  {
    id: 'idms',
    badge: 'Platform',
    title: 'Integrated Monitoring, Reporting & Project Control Platform',
    short: 'IDMS',
    tagline: 'Engineering monitoring software for instrumentation-driven project execution.',
    desc: `IDMS is built for practical instrumentation workflows in construction and infrastructure
    projects. The platform centralises real-time monitoring data, supports automated report
    generation, and provides clear project tracking views so teams can manage site status,
    submissions, and control actions from one engineering system.`,
    features: [
      'AL / WSL / PDL alert system with configurable trigger thresholds',
      'Data Center for CSV upload and external system integration',
      'Sensor model engine for instrument mapping and configuration control',
      'Virtual sensors for derived parameters and calculated engineering values',
      'Plan vs Actual tracking for instrumentation progress and site activities',
      'Submission-ready reports for consultant review and authority submissions',
    ],
    suitable: 'Main contractors, resident engineers, project owners',
    color: '#0d1b3e',
  },
  {
    id: 'nms',
    badge: 'Platform',
    title: 'Noise Monitoring System',
    short: 'NMS',
    tagline: 'Real-time construction noise monitoring supporting NEA regulatory requirements.',
    desc: `CoreVision's Noise Monitoring System (NMS) is designed for real-time construction noise
    monitoring in Singapore, supporting NEA regulatory requirements. The system records and displays
    LEQ5min, LEQ1hr, LEQ12hr, and Dose % calculations, with automated alerts and reporting to
    support site compliance monitoring.`,
    features: [
      'LEQ5min, LEQ1hr, LEQ12hr monitoring',
      'Dose % calculation based on NEA limits',
      'Real-time data transmission to cloud platform',
      'Automated alerts for exceedance risk and threshold breach',
      'NEA-format compliance reporting',
      'Multi-point site monitoring capability',
    ],
    suitable: 'Contractors, environmental consultants, and project teams requiring NEA-compliant construction noise monitoring.',
    color: '#1a3a5c',
  },
];

const Solutions: React.FC = () => {
  const [active, setActive] = useState('idms');
  const sol = SOLUTIONS.find((s) => s.id === active) || SOLUTIONS[0];

  return (
    <section id="solutions" className="cv-solutions">
      <div className="cv-container">
        <div className="cv-section-header">
          <span className="cv-section-tag">Our Platforms</span>
          <h2 className="cv-section-title">Technology-Driven Monitoring Solutions</h2>
          <p className="cv-section-subtitle">
            CoreVision's proprietary platforms are purpose-built for Singapore's construction
            industry — combining hardware expertise with enterprise-grade software.
          </p>
        </div>

        <div className="cv-solutions__tabs">
          {SOLUTIONS.map((s) => (
            <button
              key={s.id}
              className={`cv-solutions__tab${active === s.id ? ' cv-solutions__tab--active' : ''}`}
              onClick={() => setActive(s.id)}
            >
              <span className="cv-solutions__tab-short">{s.short}</span>
              <span className="cv-solutions__tab-long">{s.title}</span>
            </button>
          ))}
        </div>

        <div className="cv-solutions__panel">
          <div className="cv-solutions__panel-header">
            <span className="cv-solutions__badge">{sol.badge}</span>
            <h3 className="cv-solutions__panel-title">{sol.title}</h3>
            <p className="cv-solutions__panel-tagline">{sol.tagline}</p>
          </div>

          <div className="cv-solutions__panel-body">
            <div className="cv-solutions__desc">
              <p>{sol.desc}</p>
              <p className="cv-solutions__suitable">
                <strong>Best suited for:</strong> {sol.suitable}
              </p>
            </div>
            <div className="cv-solutions__features">
              <h4>Key Capabilities</h4>
              <ul>
                {sol.features.map((f) => (
                  <li key={f}>
                    <span className="cv-check">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="cv-solutions__why">
          <h3 className="cv-solutions__why-title">Why Choose CoreVision</h3>
          <div className="cv-solutions__why-grid">
            {[
              {
                heading: 'Engineering-Focused Solutions',
                body: 'Built for real instrumentation and monitoring workflows — not generic software platforms.',
              },
              {
                heading: 'Integrated Platform Approach',
                body: 'IDMS and NMS combine monitoring, reporting, and project tracking into a single system.',
              },
              {
                heading: 'Regulatory Alignment',
                body: 'Designed to support Singapore NEA noise monitoring requirements and compliance workflows.',
              },
              {
                heading: 'Flexible & Scalable',
                body: 'Supports multiple sensor types, projects, and integration methods across different site conditions.',
              },
              {
                heading: 'Practical Deployment Experience',
                body: 'Developed with real site operations in mind — from installation to reporting and submission.',
              },
              {
                heading: 'Data You Can Trust',
                body: 'Structured data handling, alert logic, and audit-ready reporting improve reliability and decision-making.',
              },
            ].map((item) => (
              <div key={item.heading} className="cv-solutions__why-card">
                <span className="cv-solutions__why-icon">✓</span>
                <div>
                  <h4>{item.heading}</h4>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="cv-solutions__why-stats">
            <div className="cv-solutions__why-stat">
              <span className="cv-solutions__why-stat-num">500+</span>
              <span className="cv-solutions__why-stat-label">CV-2401 Vibration Meters deployed across construction and infrastructure projects in Singapore and Malaysia</span>
            </div>
            <div className="cv-solutions__why-stat">
              <span className="cv-solutions__why-stat-num">50+</span>
              <span className="cv-solutions__why-stat-label">Noise Monitoring units supporting real-time site compliance monitoring</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
